// apps/client/src/components/common/CookieConsentBanner.tsx
// Fixed bottom banner for cookie/analytics consent — notice-and-opt-out model (AU law)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cookie, Settings, X } from 'lucide-react';
import { useCookieConsent } from '../../hooks/useCookieConsent';

const CookieConsentBanner: React.FC = () => {
  const { hasResponded, acceptAll, declineAll } = useCookieConsent();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  // Fade in after mount if the user has not responded yet
  useEffect(() => {
    if (!hasResponded) {
      // Small delay so the banner fades in rather than appearing instantly
      const timer = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(timer);
    }
  }, [hasResponded]);

  // Already responded — render nothing
  if (hasResponded) return null;

  const dismiss = (action: () => void) => {
    setExiting(true);
    // Wait for fade-out animation to finish
    setTimeout(() => {
      action();
    }, 300);
  };

  return (
    <div
      className={`fixed bottom-0 inset-x-0 z-50 p-4 transition-all duration-300 ${
        exiting
          ? 'opacity-0 translate-y-4'
          : visible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="max-w-2xl mx-auto bg-gray-800/95 backdrop-blur-md border border-gray-700 rounded-xl shadow-2xl p-5">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(37, 99, 235, 0.06))',
              }}
            >
              <Cookie className="w-5 h-5 text-blue-400" strokeWidth={1.75} />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-300 leading-relaxed">
              We use analytics to improve Valutten. No financial data is tracked.
              You can manage your preferences at any time in{' '}
              <button
                onClick={() => dismiss(() => navigate('/settings?tab=privacy'))}
                className="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
              >
                Settings
              </button>
              .
            </p>

            {/* Actions */}
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={() => dismiss(acceptAll)}
                className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              >
                Accept
              </button>
              <button
                onClick={() => dismiss(() => navigate('/settings?tab=privacy'))}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-gray-600 text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
              >
                <Settings className="w-3.5 h-3.5" />
                Manage
              </button>
            </div>
          </div>

          {/* Close / decline */}
          <button
            onClick={() => dismiss(declineAll)}
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-gray-700/50 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
